"use client";

import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import type { JEOPARDY_GAME_MAKER_STRINGS } from "@/components/tools/jeopardy-game-maker";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  clueCount,
  MAX_CATEGORIES,
  MAX_ROWS,
  MAX_TEAMS,
  type ParsedBoard,
  POINT_STEPS,
} from "@/lib/tool-kit/games-organizers/jeopardy";

type Strings = typeof JEOPARDY_GAME_MAKER_STRINGS;

export interface JeopardySettings {
  text: string;
  step: number;
  teams: number;
  names: string;
  remember: boolean;
}

const TEAM_CHOICES = Array.from({ length: MAX_TEAMS }, (_, i) => i + 1);

const PROBLEM_TEXT = {
  "no-category": "problemNoCategory",
  "empty-clue": "problemEmptyClue",
  "empty-category": "problemEmptyCategory",
  "extra-category": "problemExtraCategory",
  "extra-clue": "problemExtraClue",
} as const;

/** The board text and the game options, before play starts. */
export default function JeopardySetup({
  settings,
  board,
  error,
  strings,
  onChange,
  onRemember,
  onStart,
  onClear,
}: {
  settings: JeopardySettings;
  board: ParsedBoard;
  error: boolean;
  strings: Strings;
  onChange: (patch: Partial<JeopardySettings>) => void;
  onRemember: (on: boolean) => void;
  onStart: () => void;
  onClear: () => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const total = clueCount(board.categories);
  return (
    <div className="space-y-4">
      <TextAreaField
        hint={
          <>
            {t(strings.boardHint, {
              maxCategories: MAX_CATEGORIES,
              maxRows: MAX_ROWS,
            })}{" "}
            {total > 0
              ? t(strings.summary, {
                  categories: tPlural(
                    strings.categoriesCount,
                    locale,
                    board.categories.length,
                    { count: fmt(board.categories.length) }
                  ),
                  clues: tPlural(strings.cluesCount, locale, total, {
                    count: fmt(total),
                  }),
                })
              : ""}
          </>
        }
        id="jeopardy-board"
        label={strings.boardLabel}
        onChange={(text) => onChange({ text })}
        placeholder={strings.boardPlaceholder}
        prose
        rows={12}
        value={settings.text}
      />
      <button
        className={BUTTON_SMALL}
        onClick={() => onChange({ text: strings.example })}
        type="button"
      >
        {strings.loadExample}
      </button>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.stepHint}
          id="jeopardy-step"
          label={strings.stepLabel}
          onChange={(value) => onChange({ step: Number(value) })}
          options={POINT_STEPS.map((n) => ({
            value: String(n),
            label: fmt(n),
          }))}
          value={String(settings.step)}
        />
        <SelectField
          id="jeopardy-teams"
          label={strings.teamsLabel}
          onChange={(value) => onChange({ teams: Number(value) })}
          options={TEAM_CHOICES.map((n) => ({
            value: String(n),
            label: tPlural(strings.teamsOption, locale, n, { count: fmt(n) }),
          }))}
          value={String(settings.teams)}
        />
      </div>
      <TextField
        id="jeopardy-names"
        label={strings.namesLabel}
        onChange={(names) => onChange({ names })}
        placeholder={strings.namesPlaceholder}
        value={settings.names}
      />
      <Checkbox
        checked={settings.remember}
        label={strings.remember}
        onChange={onRemember}
      />
      <p className={HINT}>{strings.rememberNote}</p>
      {board.problems.map((problem) => (
        <Notice key={`${problem.line}-${problem.kind}`}>
          {t(strings[PROBLEM_TEXT[problem.kind]], {
            line: fmt(problem.line),
            max: problem.kind === "extra-clue" ? MAX_ROWS : MAX_CATEGORIES,
          })}
        </Notice>
      ))}
      {error ? <ErrorNotice>{strings.needBoard}</ErrorNotice> : null}
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={onStart} type="button">
          {strings.start}
        </button>
        <button className={BUTTON_SECONDARY} onClick={onClear} type="button">
          {strings.clearSaved}
        </button>
      </div>
    </div>
  );
}
